import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Brain, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  Lightbulb,
  RefreshCw,
  ChevronRight,
  Zap,
  Lock
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { handleAIError } from '@/hooks/useAIRequest';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useNavigate } from 'react-router-dom';

interface PredictiveAnalysis {
  status: 'on_track' | 'at_risk' | 'delayed';
  summary: string;
  diagnosis: string;
  suggestions: string[];
  predictedDelayDays: number;
  metadata: {
    totalTasks: number;
    completedTasks: number;
    inProgressTasks: number;
    overdueTasks: number;
    weeklyVelocity: number;
    activeProjects: number;
    criticalProject: string | null;
    analyzedAt: string;
  };
}

interface PredictiveAIWidgetProps {
  isLoaded?: boolean;
}

export function PredictiveAIWidget({ isLoaded = true }: PredictiveAIWidgetProps) {
  const [analysis, setAnalysis] = useState<PredictiveAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { plan, isAdmin } = usePlanLimits();
  const navigate = useNavigate();
  
  const isPremiumLocked = plan !== 'profissional' && !isAdmin;

  const fetchAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error: responseError } = await supabase.functions.invoke('ai-predict-delays', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      // Handle errors with upsell logic
      if (responseError) {
        const errorMessage = responseError?.message || JSON.stringify(responseError);
        
        // Check for plan-related errors
        if (handleAIError({ message: errorMessage, body: data }, navigate)) {
          setError('Funcionalidade premium');
          return;
        }
        
        if (errorMessage.toLowerCase().includes('unauthorized') || 
            errorMessage.toLowerCase().includes('session')) {
          console.warn('Session expired in PredictiveAIWidget, signing out...');
          await supabase.auth.signOut();
          return;
        }
        throw responseError;
      }

      // Check if response contains plan access error
      if (data?.error === 'PLAN_ACCESS_DENIED' || data?.upgrade_required) {
        handleAIError({ body: data }, navigate);
        setError('Funcionalidade premium');
        return;
      }
      
      // Also check for session_expired in response data
      if (data?.error === 'Unauthorized' || data?.session_expired) {
        console.warn('Session expired (from response), signing out...');
        await supabase.auth.signOut();
        return;
      }
      
      setAnalysis(data);
    } catch (err: unknown) {
      const error = err as Error & { body?: Record<string, unknown> };
      const errorMessage = error?.message || String(err);
      
      // Try to handle as AI error
      if (handleAIError(err, navigate)) {
        setError('Funcionalidade premium');
        return;
      }
      
      if (errorMessage.toLowerCase().includes('unauthorized') || 
          errorMessage.toLowerCase().includes('session')) {
        console.warn('Session expired in PredictiveAIWidget catch, signing out...');
        await supabase.auth.signOut();
        return;
      }
      console.error('Error fetching prediction:', err);
      setError(errorMessage || 'Erro ao analisar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoaded) {
      fetchAnalysis();
    }
  }, [isLoaded]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'on_track':
        return {
          icon: CheckCircle,
          color: 'text-emerald-500',
          bgColor: 'bg-emerald-500/10',
          borderColor: 'border-emerald-500/20',
          label: 'No prazo'
        };
      case 'at_risk':
        return {
          icon: Clock,
          color: 'text-amber-500',
          bgColor: 'bg-amber-500/10',
          borderColor: 'border-amber-500/20',
          label: 'Em risco'
        };
      case 'delayed':
        return {
          icon: AlertTriangle,
          color: 'text-rose-500',
          bgColor: 'bg-rose-500/10',
          borderColor: 'border-rose-500/20',
          label: 'Atrasado'
        };
      default:
        return {
          icon: Brain,
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary/20',
          label: 'Analisando'
        };
    }
  };

  const statusConfig = analysis ? getStatusConfig(analysis.status) : getStatusConfig('');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <Card className="bento-card overflow-hidden">
        <CardHeader className="pb-3 bg-gradient-to-r from-violet-500/5 to-purple-500/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                <Brain className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  IA Preditiva
                  {isPremiumLocked && (
                    <Lock className="h-4 w-4 text-amber-500" />
                  )}
                </CardTitle>
                <p className="text-xs text-muted-foreground">Análise automática de atrasos</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={fetchAnalysis}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="pt-4">
          {loading && !analysis ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="relative">
                <div className="h-12 w-12 rounded-full border-2 border-violet-500/20 animate-pulse" />
                <Brain className="h-6 w-6 text-violet-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
              </div>
              <p className="text-sm text-muted-foreground mt-3">Analisando seus projetos...</p>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchAnalysis} className="mt-3">
                Tentar novamente
              </Button>
            </div>
          ) : analysis ? (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className={`flex items-center gap-3 p-3 rounded-xl ${statusConfig.bgColor} border ${statusConfig.borderColor}`}>
                <statusConfig.icon className={`h-5 w-5 ${statusConfig.color}`} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${statusConfig.color}`}>
                      {statusConfig.label}
                    </span>
                    {analysis.predictedDelayDays > 0 && (
                      <span className="text-xs font-bold text-rose-500">
                        +{analysis.predictedDelayDays} dias
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{analysis.summary}</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 rounded-lg bg-muted/50">
                  <p className="text-lg font-bold text-foreground">{analysis.metadata.weeklyVelocity}</p>
                  <p className="text-[10px] text-muted-foreground">tarefas/semana</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-muted/50">
                  <p className="text-lg font-bold text-foreground">{analysis.metadata.overdueTasks}</p>
                  <p className="text-[10px] text-muted-foreground">atrasadas</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-muted/50">
                  <p className="text-lg font-bold text-foreground">{analysis.metadata.activeProjects}</p>
                  <p className="text-[10px] text-muted-foreground">projetos</p>
                </div>
              </div>

              {/* Diagnosis */}
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {analysis.diagnosis}
                </p>
              </div>

              {/* Suggestions Toggle */}
              {analysis.suggestions && analysis.suggestions.length > 0 && (
                <div>
                  <Button
                    variant="ghost"
                    className="w-full justify-between h-auto py-2 px-3"
                    onClick={() => setShowSuggestions(!showSuggestions)}
                  >
                    <div className="flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-amber-500" />
                      <span className="text-sm">Sugestões de aceleração</span>
                    </div>
                    <ChevronRight className={`h-4 w-4 transition-transform ${showSuggestions ? 'rotate-90' : ''}`} />
                  </Button>

                  <AnimatePresence>
                    {showSuggestions && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 pt-2">
                          {analysis.suggestions.map((suggestion, index) => (
                            <motion.div
                              key={index}
                              initial={{ x: -10, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: index * 0.1 }}
                              className="flex items-start gap-2 p-2 rounded-lg bg-gradient-to-r from-amber-500/5 to-transparent"
                            >
                              <Zap className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                              <p className="text-xs text-muted-foreground">{suggestion}</p>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Last Updated */}
              <p className="text-[10px] text-muted-foreground text-center">
                Atualizado em {new Date(analysis.metadata.analyzedAt).toLocaleString('pt-BR')}
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </motion.div>
  );
}
